import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback, useMemo, useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import BrowserOnly from 'components/consumer/BrowserOnly';
import PDPFullScreenGallery from 'components/consumer/PDP/PDPFullScreenGallery';
import PDPView3DButton from 'components/consumer/PDP/PDPView3DButton';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import PDPMobileGalleryZoomIcon from 'components/consumer/PDP/PDPMobileGalleryZoomIcon';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import CSSScrollSnapCarousel from 'components/consumer/CSSScrollSnapCarousel';
import scrollIntoViewUtil from 'commons/scrollIntoViewUtil';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import PDPShownIn from '../PDPShownIn';
const CarouselSelectedIndexContext = React.createContext(0);
const PDPCarouselIntroVideoSlide = t0 => {
  const $ = _c(2);
  const {
    slideIndex,
    videoId
  } = t0;
  const selectedCarouselIndex = useContext(CarouselSelectedIndexContext);
  if (slideIndex !== selectedCarouselIndex) {
    return null;
  }
  let t1;
  if ($[0] !== videoId) {
    t1 = <PDPProductIntroVideo videoId={videoId} />;
    $[0] = videoId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
PDPCarouselIntroVideoSlide.propTypes = {
  slideIndex: PropTypes.number.isRequired,
  videoId: PropTypes.string.isRequired
};

// Return items that have a unique id or videoId field
const getUniqueCarouselItems = items => {
  const uniqueItems = [];
  const seenIds = new Set();
  items.forEach(item => {
    const id = item.id || item.videoId;
    if (id && !seenIds.has(id)) {
      seenIds.add(id);
      uniqueItems.push(item);
    }
  });
  return uniqueItems;
};
const DEFAULT_PHOTOS = [];
const PDPMobileCarousel = t0 => {
  const $ = _c(110);
  const {
    displayShownIn: t1,
    enableCrops: t2,
    headerLeft,
    isConfigFlipped: t3,
    isEditOrder: t4,
    isHero: t5,
    maxPhotos: t6,
    onAfterSlide,
    onCloseFullScreenGallery,
    onOpenFullScreenGallery,
    onPhotoViewed,
    photos: t7,
    productName,
    rightButtons,
    showARIcon: t8,
    showDecideLaterOverlay: t9,
    showFavoriteButton: t10,
    videoId,
    zoomOnClick: t11
  } = t0;
  const displayShownIn = t1 === undefined ? false : t1;
  const enableCrops = t2 === undefined ? true : t2;
  const isConfigFlipped = t3 === undefined ? false : t3;
  const isEditOrder = t4 === undefined ? false : t4;
  const isHero = t5 === undefined ? false : t5;
  const maxPhotos = t6 === undefined ? 16 : t6;
  const photos = t7 === undefined ? DEFAULT_PHOTOS : t7;
  const showARIcon = t8 === undefined ? false : t8;
  const showDecideLaterOverlay = t9 === undefined ? false : t9;
  const showFavoriteButton = t10 === undefined ? false : t10;
  const zoomOnClick = t11 === undefined ? true : t11;
  const validPhotos = useRemoveBrokenImageUrls(photos);
  const isConfigurationGallery = validPhotos?.[0]?.type === "configuration" || validPhotos?.[0]?.type === "cylindo";
  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0);
  let t12;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {};
    $[0] = t12;
  } else {
    t12 = $[0];
  }
  const [brokenImages, setBrokenImages] = useState(t12);
  const hasMoreThanMaxPhotos = validPhotos.length > maxPhotos;
  let t13;
  if ($[1] !== hasMoreThanMaxPhotos || $[2] !== maxPhotos || $[3] !== validPhotos || $[4] !== videoId) {
    const trimmedPhotos = hasMoreThanMaxPhotos ? validPhotos.slice(0, maxPhotos) : validPhotos;
    t13 = videoId && trimmedPhotos.length ? [trimmedPhotos[0], {
      videoId
    }, ...trimmedPhotos.slice(1)] : trimmedPhotos;
    $[1] = hasMoreThanMaxPhotos;
    $[2] = maxPhotos;
    $[3] = validPhotos;
    $[4] = videoId;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  const photosAndVideo = t13;
  const carouselPhotos = photosAndVideo;
  const standardRatio = isConfigurationGallery ? 0.6666666666666666 : 0.5;
  let t14;
  if ($[6] !== displayShownIn) {
    t14 = () => {
      if (!displayShownIn) {
        return;
      }
      scrollIntoViewUtil("[data-xc=\"PDPMobileConfigurationPhotoGalleryUI\"]", 0);
    };
    $[6] = displayShownIn;
    $[7] = t14;
  } else {
    t14 = $[7];
  }
  const scrollDownToCustomizer = t14;
  let t15;
  if ($[8] !== carouselPhotos) {
    t15 = getUniqueCarouselItems(carouselPhotos);
    $[8] = carouselPhotos;
    $[9] = t15;
  } else {
    t15 = $[9];
  }
  const uniqueCarouselPhotos = t15;
  let t16;
  if ($[10] !== onOpenFullScreenGallery || $[11] !== uniqueCarouselPhotos) {
    t16 = carouselIndex => {
      setIsFullScreenGalleryOpen(true);
      onOpenFullScreenGallery?.(uniqueCarouselPhotos[carouselIndex]);
    };
    $[10] = onOpenFullScreenGallery;
    $[11] = uniqueCarouselPhotos;
    $[12] = t16;
  } else {
    t16 = $[12];
  }
  const openFullScreenGallery = t16;
  let t17;
  if ($[13] !== onAfterSlide || $[14] !== uniqueCarouselPhotos) {
    t17 = slideIndex => {
      setSelectedCarouselIndex(slideIndex);
      onAfterSlide?.(uniqueCarouselPhotos[slideIndex]);
    };
    $[13] = onAfterSlide;
    $[14] = uniqueCarouselPhotos;
    $[15] = t17;
  } else {
    t17 = $[15];
  }
  const handleSlideChange = t17;
  let t18;
  if ($[16] !== uniqueCarouselPhotos) {
    t18 = uniqueCarouselPhotos.findIndex(_temp);
    $[16] = uniqueCarouselPhotos;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  const videoSlideIndex = t18;
  let t19;
  if ($[18] !== selectedCarouselIndex || $[19] !== uniqueCarouselPhotos || $[20] !== validPhotos) {
    let t20;
    if ($[22] !== selectedCarouselIndex || $[23] !== uniqueCarouselPhotos) {
      t20 = t21 => {
        const {
          src
        } = t21;
        return src === uniqueCarouselPhotos[selectedCarouselIndex]?.src;
      };
      $[22] = selectedCarouselIndex;
      $[23] = uniqueCarouselPhotos;
      $[24] = t20;
    } else {
      t20 = $[24];
    }
    t19 = validPhotos.findIndex(t20);
    $[18] = selectedCarouselIndex;
    $[19] = uniqueCarouselPhotos;
    $[20] = validPhotos;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  const fullScreenPhotoIndex = Math.max(0, t19);
  const isVideoSlideSelected = videoSlideIndex !== -1 && selectedCarouselIndex === videoSlideIndex;
  let t20;
  if ($[25] !== brokenImages || $[26] !== displayShownIn || $[27] !== enableCrops || $[28] !== hasMoreThanMaxPhotos || $[29] !== isConfigFlipped || $[30] !== isConfigurationGallery || $[31] !== isEditOrder || $[32] !== isHero || $[33] !== openFullScreenGallery || $[34] !== productName || $[35] !== scrollDownToCustomizer || $[36] !== showDecideLaterOverlay || $[37] !== standardRatio || $[38] !== uniqueCarouselPhotos || $[39] !== validPhotos.length || $[40] !== zoomOnClick) {
    let t21;
    if ($[42] !== brokenImages || $[43] !== displayShownIn || $[44] !== enableCrops || $[45] !== hasMoreThanMaxPhotos || $[46] !== isConfigFlipped || $[47] !== isConfigurationGallery || $[48] !== isEditOrder || $[49] !== isHero || $[50] !== openFullScreenGallery || $[51] !== productName || $[52] !== scrollDownToCustomizer || $[53] !== showDecideLaterOverlay || $[54] !== standardRatio || $[55] !== uniqueCarouselPhotos.length || $[56] !== validPhotos.length || $[57] !== zoomOnClick) {
      t21 = (t22, index) => {
        const {
          src: src_0,
          id,
          type,
          cropData,
          videoId: videoId_0,
          optionId,
          upholsteryName,
          valueId
        } = t22;
        const slideKey = videoId_0 ? `video-${videoId_0}` : `${id || src_0 || index}`;
        return {
          key: slideKey,
          slide: <RatioContainer className={classNames({
            "pt-8 px-6 pb-11": isConfigurationGallery && !enableCrops,
            "scale-x-[-1]": isConfigurationGallery && isConfigFlipped,
            "flex justify-center": brokenImages[src_0]
          })} ratio={enableCrops ? 1 : standardRatio}>{videoId_0 ? <PDPCarouselIntroVideoSlide slideIndex={index} videoId={videoId_0} /> : <>{displayShownIn ? <PDPShownIn isEditOrder={isEditOrder} optionId={optionId} upholsteryName={upholsteryName} valueId={valueId} layout="roundSwatchLabel" onSelection={scrollDownToCustomizer} productName={productName} showInactiveUpholstery={true} /> : null}<JBProductImage className="h-full object-contain w-full" {...getMainImageImgixPropsForPhoto({
                src: src_0,
                type,
                cropData
              }, enableCrops)} lazy={index !== 0} priority={index === 0 && isHero} htmlAttributes={index === 0 && isHero ? {
                "data-xc": "PDPMobileStudioHero"
              } : undefined} sizes="100vw" onClick={zoomOnClick ? showDecideLaterOverlay ? _temp2 : () => {
                setSelectedCarouselIndex(index);
                openFullScreenGallery(index);
              } : undefined} forPage="pdp" onLoad={isFail => {
                if (index === 0 && isHero) {
                  markLuxPageNavigationEnd();
                }
                if (!isFail) {
                  return;
                }
                setBrokenImages({
                  ...brokenImages,
                  [src_0]: true
                });
              }} /></>}{hasMoreThanMaxPhotos && index === uniqueCarouselPhotos.length - 1 && <div className="items-center bottom-0 flex justify-center left-0 pointer-events-none absolute right-0 top-0"><div className="text-lg font-bold bg-brand-lightest text-brand-dark px-4 py-3">Explore{" "}{validPhotos.length - uniqueCarouselPhotos.length}+ more photos</div></div>}</RatioContainer>
        };
      };
      $[42] = brokenImages;
      $[43] = displayShownIn;
      $[44] = enableCrops;
      $[45] = hasMoreThanMaxPhotos;
      $[46] = isConfigFlipped;
      $[47] = isConfigurationGallery;
      $[48] = isEditOrder;
      $[49] = isHero;
      $[50] = openFullScreenGallery;
      $[51] = productName;
      $[52] = scrollDownToCustomizer;
      $[53] = showDecideLaterOverlay;
      $[54] = standardRatio;
      $[55] = uniqueCarouselPhotos.length;
      $[56] = validPhotos.length;
      $[57] = zoomOnClick;
      $[58] = t21;
    } else {
      t21 = $[58];
    }
    t20 = uniqueCarouselPhotos.map(t21).filter(Boolean);
    $[25] = brokenImages;
    $[26] = displayShownIn;
    $[27] = enableCrops;
    $[28] = hasMoreThanMaxPhotos;
    $[29] = isConfigFlipped;
    $[30] = isConfigurationGallery;
    $[31] = isEditOrder;
    $[32] = isHero;
    $[33] = openFullScreenGallery;
    $[34] = productName;
    $[35] = scrollDownToCustomizer;
    $[36] = showDecideLaterOverlay;
    $[37] = standardRatio;
    $[38] = uniqueCarouselPhotos;
    $[39] = validPhotos.length;
    $[40] = zoomOnClick;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const slides = t20;
  let t21;
  if ($[59] !== handleSlideChange || $[60] !== slides) {
    t21 = <CSSScrollSnapCarousel onSlideChange={handleSlideChange} slides={slides} />;
    $[59] = handleSlideChange;
    $[60] = slides;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== selectedCarouselIndex || $[63] !== t21) {
    t22 = <CarouselSelectedIndexContext.Provider value={selectedCarouselIndex}>{t21}</CarouselSelectedIndexContext.Provider>;
    $[62] = selectedCarouselIndex;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  const t23 = !displayShownIn;
  const t24 = !showDecideLaterOverlay;
  let t25;
  if ($[65] !== displayShownIn || $[66] !== showDecideLaterOverlay || $[67] !== t23 || $[68] !== t24) {
    t25 = classNames("absolute right-4 top-4 z-[1]", {
      "justify-between left-4": t23,
      "justify-end": displayShownIn,
      flex: t24,
      "!hidden": showDecideLaterOverlay
    });
    $[65] = displayShownIn;
    $[66] = showDecideLaterOverlay;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== displayShownIn || $[71] !== headerLeft) {
    t26 = !displayShownIn ? <div className="pointer-events-none">{!!headerLeft && headerLeft}</div> : null;
    $[70] = displayShownIn;
    $[71] = headerLeft;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== showARIcon) {
    t27 = showARIcon && <PDPView3DButton />;
    $[73] = showARIcon;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== isConfigurationGallery || $[76] !== isVideoSlideSelected || $[77] !== openFullScreenGallery || $[78] !== selectedCarouselIndex || $[79] !== zoomOnClick) {
    t28 = !!zoomOnClick && !isVideoSlideSelected && <PDPMobileGalleryZoomIcon inverted={isConfigurationGallery} onClick={() => openFullScreenGallery(selectedCarouselIndex)} />;
    $[75] = isConfigurationGallery;
    $[76] = isVideoSlideSelected;
    $[77] = openFullScreenGallery;
    $[78] = selectedCarouselIndex;
    $[79] = zoomOnClick;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  let t29;
  if ($[81] !== showFavoriteButton) {
    t29 = showFavoriteButton && <PDPFavoriteButton size="sm" className="ml-0" />;
    $[81] = showFavoriteButton;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  const t30 = !!rightButtons && rightButtons;
  let t31;
  if ($[83] !== t29 || $[84] !== t30) {
    t31 = <div className="flex flex-col gap-2">{t29}{t30}</div>;
    $[83] = t29;
    $[84] = t30;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] !== t27 || $[87] !== t28 || $[88] !== t31) {
    t32 = <div className="flex [&_>_*_+_*]:ml-2.5">{t27}{t28}{t31}</div>;
    $[86] = t27;
    $[87] = t28;
    $[88] = t31;
    $[89] = t32;
  } else {
    t32 = $[89];
  }
  let t33;
  if ($[90] !== t25 || $[91] !== t26 || $[92] !== t32) {
    t33 = <div className={t25}>{t26}{t32}</div>;
    $[90] = t25;
    $[91] = t26;
    $[92] = t32;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== t22 || $[95] !== t33) {
    t34 = <div className="relative">{t22}{t33}</div>;
    $[94] = t22;
    $[95] = t33;
    $[96] = t34;
  } else {
    t34 = $[96];
  }
  let t35;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => {
      setIsFullScreenGalleryOpen(false);
    };
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  let t36;
  if ($[98] !== displayShownIn || $[99] !== fullScreenPhotoIndex || $[100] !== isEditOrder || $[101] !== isFullScreenGalleryOpen || $[102] !== onCloseFullScreenGallery || $[103] !== onPhotoViewed || $[104] !== productName || $[105] !== validPhotos) {
    t36 = <BrowserOnly><PDPFullScreenGallery isEditOrder={isEditOrder} isOpen={isFullScreenGalleryOpen} photos={validPhotos} initialSelectedPhotoIndex={fullScreenPhotoIndex} onClose={t35} onDismiss={onCloseFullScreenGallery} onPhotoViewed={onPhotoViewed} productName={productName} displayShownIn={displayShownIn} /></BrowserOnly>;
    $[98] = displayShownIn;
    $[99] = fullScreenPhotoIndex;
    $[100] = isEditOrder;
    $[101] = isFullScreenGalleryOpen;
    $[102] = onCloseFullScreenGallery;
    $[103] = onPhotoViewed;
    $[104] = productName;
    $[105] = validPhotos;
    $[106] = t36;
  } else {
    t36 = $[106];
  }
  let t37;
  if ($[107] !== t34 || $[108] !== t36) {
    t37 = <>{t34}{t36}</>;
    $[107] = t34;
    $[108] = t36;
    $[109] = t37;
  } else {
    t37 = $[109];
  }
  return t37;
};
PDPMobileCarousel.propTypes = {
  displayShownIn: PropTypes.bool,
  enableCrops: PropTypes.bool,
  headerLeft: PropTypes.node,
  isConfigFlipped: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isHero: PropTypes.bool,
  maxPhotos: PropTypes.number,
  onAfterSlide: PropTypes.func,
  // User closed the full-screen gallery (not by choosing a fabric)
  onCloseFullScreenGallery: PropTypes.func,
  onOpenFullScreenGallery: PropTypes.func,
  onPhotoViewed: PropTypes.func,
  onDimensionsIconClick: PropTypes.func,
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    src: PropTypes.string,
    type: PropTypes.string
  })),
  productName: PropTypes.string,
  rightButtons: PropTypes.node,
  showARIcon: PropTypes.bool,
  showDecideLaterOverlay: PropTypes.bool,
  showFavoriteButton: PropTypes.bool,
  videoId: PropTypes.string,
  zoomOnClick: PropTypes.bool
};
export default PDPMobileCarousel;
function _temp(item) {
  return item.videoId;
}
function _temp2() {}